import { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import ProductCard from '../components/ui/ProductCard.jsx';
import SectionState from '../components/ui/SectionState.jsx';
import Pagination from '../components/ui/Pagination.jsx';
import { useApi } from '../hooks/useApi.js';
import { getProducts } from '../api/productsApi.js';
import { getCategories, getBrands } from '../api/catalogApi.js';
import { trackFilter, trackSearch } from '../utils/analytics.js';
import './ShopPage.css';

// 有獨立頁的分類：側邊欄點了直接跳該頁，不在商品一覽內篩選
// （dart-card 已改成在商品一覽內，所以不列入；只有 virtual 維持獨立頁）
const STANDALONE_CATEGORY_LINKS = { virtual: '/virtual' };

const PAGE_SIZE = 9;

const SORT_OPTIONS = [
  { value: 'newest',     label: '最新上架' },
  { value: 'popular',    label: '熱門度' },
  { value: 'price-asc',  label: '價格低 → 高' },
  { value: 'price-desc', label: '價格高 → 低' },
];

// 標籤篩選：對應商品 tags 欄位（與 ProductCard 的徽章一致：best 顯示為 HOT）
const TAG_OPTIONS = [
  { value: 'new',  label: 'NEW' },
  { value: 'best', label: 'HOT' },
  { value: 'sale', label: 'SALE' },
];

function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryId = searchParams.get('category') || null;
  const brand = searchParams.get('brand') || null;

  const [keyword, setKeyword] = useState('');
  const [sort, setSort] = useState('newest');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [tag, setTag] = useState(null);

  const [page, setPage] = useState(1);

  const { data: categories = [] } = useApi(() => getCategories(), []);
  const { data: brands = [] }     = useApi(() => getBrands(), []);

  // 後端分頁：分類 / 品牌 / 關鍵字 / 排序 / 標籤 / 頁碼任一改變都重新 fetch
  const { data: res, loading, error, refetch } = useApi(
    () => getProducts({ categoryId, brand, search: keyword, sort, tag, page, limit: PAGE_SIZE }),
    [categoryId, brand, keyword, sort, tag, page]
  );

  // 換條件（非換頁）→ 回第一頁
  useEffect(() => { setPage(1); }, [categoryId, brand, keyword, sort, tag]);

  useEffect(() => {
    const timer = setTimeout(() => trackSearch(keyword.trim(), 'shop'), 500);
    return () => clearTimeout(timer);
  }, [keyword]);

  useEffect(() => {
    trackFilter('category', categoryId || 'all', 'shop');
    trackFilter('brand', brand || 'all', 'shop');
    trackFilter('tag', tag || 'all', 'shop');
    trackFilter('sort', sort, 'shop');
  }, [categoryId, brand, tag, sort]);

  const items = res?.items || [];
  const total = res?.total || 0;
  const totalPages = res?.totalPages || 1;
  const pageStart = (page - 1) * PAGE_SIZE;
  // res 在 refetch 期間保留前一次結果（useApi 不重設 data），換頁不閃骨架
  const firstLoading = loading && !res;
  const goPage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // 排序選單：點選單外或按 Esc 關閉
  const sortRef = useRef(null);
  useEffect(() => {
    if (!showSortMenu) return;
    const onDoc = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setShowSortMenu(false); };
    const onKey = (e) => { if (e.key === 'Escape') setShowSortMenu(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [showSortMenu]);

  const activeFacet = categoryId || brand || 'all';

  const setFacet = (kind, value) => {
    const next = new URLSearchParams(searchParams);
    next.delete('category');
    next.delete('brand');
    if (kind && value) next.set(kind, value);
    setSearchParams(next, { replace: true });
  };

  const breadcrumb = useMemo(() => {
    const cat = categories.find(c => c.id === categoryId);
    const br  = brands.find(b => b.id === brand);
    if (cat) return <>首頁 / 商品一覽 / <b>{cat.name} {cat.en}</b></>;
    if (br)  return <>首頁 / 商品一覽 / <b>{br.name}</b></>;
    return <>首頁 / <b>商品一覽</b></>;
  }, [categories, brands, categoryId, brand]);

  return (
    <main className="plp">
      <p className="crumbs">{breadcrumb}</p>

      <div className="plp-head">
        <div>
          <h1>商品一覽 · ALL DARTS</h1>
          <p className="count">共 {total} 件商品</p>
        </div>
        <div className="plp-controls" style={{ margin: 0 }}>
          <div className="search">
            <Icon name="search" size={18} />
            <input
              placeholder="搜尋商品 · 選手名 · 品牌"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />
          </div>
          <div className="sort">
            <span className="sort-label">排序</span>
            <div style={{ position: 'relative' }} ref={sortRef}>
              <button
                className="sort-btn"
                aria-haspopup="listbox"
                aria-expanded={showSortMenu}
                onClick={() => setShowSortMenu(v => !v)}
              >
                {SORT_OPTIONS.find(o => o.value === sort)?.label}
                <Icon name="chevron" size={14} />
              </button>
              {showSortMenu && (
                <div className="sort-menu">
                  {SORT_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      className={o.value === sort ? 'on' : ''}
                      onClick={() => { setSort(o.value); setShowSortMenu(false); }}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="plp-grid">
        {/* Sidebar filters */}
        <aside className="plp-side">
          <div className="filter-group">
            <h4>分類</h4>
            <div className="filter-chips">
              <button
                className={`filter-chip${activeFacet === 'all' ? ' on' : ''}`}
                onClick={() => setFacet(null)}
              >全部</button>
              {categories.map(c => {
                const standalone = STANDALONE_CATEGORY_LINKS[c.id];
                // 有獨立頁的分類（虛擬商品）→ 跳轉該頁，不在本頁篩選
                if (standalone) {
                  return (
                    <Link key={c.id} to={standalone} className="filter-chip">
                      {c.name}
                    </Link>
                  );
                }
                return (
                  <button
                    key={c.id}
                    className={`filter-chip${categoryId === c.id ? ' on' : ''}`}
                    onClick={() => setFacet('category', c.id)}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="filter-group">
            <h4>品牌</h4>
            <div className="filter-checks">
              {brands.map(b => (
                <label key={b.id}>
                  <input
                    type="checkbox"
                    checked={brand === b.id}
                    onChange={() => setFacet(brand === b.id ? null : 'brand', b.id)}
                  />
                  {b.name}
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h4>標籤</h4>
            <div className="filter-chips">
              <button
                className={`filter-chip${tag === null ? ' on' : ''}`}
                onClick={() => setTag(null)}
              >全部</button>
              {TAG_OPTIONS.map(o => (
                <button
                  key={o.value}
                  className={`filter-chip${tag === o.value ? ' on' : ''}`}
                  onClick={() => setTag(tag === o.value ? null : o.value)}
                >{o.label}</button>
              ))}
            </div>
          </div>

          <button
            className="filter-reset"
            onClick={() => { setFacet(null); setTag(null); setKeyword(''); setSort('newest'); }}
          >重置全部</button>
        </aside>

        {/* Results grid */}
        <div>
          <div className="results-meta">
            <div className="count">共 <b>{total}</b> 項商品</div>
            {total > 0 && <div>顯示 {pageStart + 1}–{pageStart + items.length}</div>}
          </div>
          {error ? (
            <SectionState error={error} onRetry={refetch} />
          ) : firstLoading ? (
            <div className="plp-results">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="product" style={{ minHeight: 280, background: 'var(--ice)', borderRadius: 14 }} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <SectionState emptyText="沒有符合條件的商品。" />
          ) : (
            <>
              <div className="plp-results">
                {items.map(p => <ProductCard key={p.id} product={p} />)}
              </div>
              <Pagination page={page} totalPages={totalPages} onChange={goPage} />
            </>
          )}
        </div>
      </div>
    </main>
  );
}

export default ShopPage;
